import { useUserStore } from "@/store/user";
import { useSearchStore } from "~/store/search";
import { useSearchFiltersStore } from "~/store/searchFilter";
import RequestQuery from "~/types/RequestQueryType";
import { requestQueryDateParser } from "~/utils/RequestQueryDateParser";
import { useSocialStore } from "@/store/social";

export const useRequestQuery = (
  searchType: string,
  replaceSearchQuery: string | null = null,
  profileId: string | null = null,
) => {
  const userStore = useUserStore();
  const savedSearchStore = useSearchStore();
  const searchFiltersStore = useSearchFiltersStore();
  const currentSearchFilter = searchFiltersStore.getCurrentSearchFilter;
  const socialStore = useSocialStore();

  const addArticleSearchFields = (query: RequestQuery) => {
    query.data.id = null;
    query.data.attributes.limit = savedSearchStore.limit;
    query.data.attributes.page = savedSearchStore.page;
    query.data.attributes.sort = userStore.getMentionsMonitorSort;
  };

  const addChartSearchFields = (query: RequestQuery) => {
    query.data.attributes.aggregation_fields =
      currentSearchFilter.aggregation_fields;
    query.data.attributes.aggregation_scale =
      currentSearchFilter.aggregation_scale;
  };

  const addCsvArticleSearchFields = (query: RequestQuery) => {
    query.data.attributes.limit = 1000;
    query.data.attributes.page = 1;
    query.data.attributes.search_type = 2;
    query.data.attributes.sort = userStore.getMentionsMonitorSort;
  };

  const addTopicSearchFields = (query: RequestQuery) => {
    query.data.attributes.aggregation_scale =
      currentSearchFilter.aggregation_scale;
    delete query.data.attributes.search_type;
  };

  const getRelationships = () => {
    let relationshipObj = {};
    if (savedSearchStore.querySearchType !== 14) {
      relationshipObj = {
        profile: {
          data: {
            type: "profiles",
            id: profileId || savedSearchStore.selectedNestedSearch.id,
          },
        },
      };
    }

    return relationshipObj;
  };

  const RequestQuery: RequestQuery = {
    data: {
      attributes: {
        podcast_media_type: searchFiltersStore.hasPodcastMediaTypeAccess
          ? currentSearchFilter.podcast_media_type
          : false,
        podcast_categories: searchFiltersStore.hasPodcastMediaTypeAccess
          ? currentSearchFilter.podcast_categories
          : [],
        search_type: savedSearchStore.querySearchType,
        search_text: replaceSearchQuery || savedSearchStore.searchText, // update store searchText whenever generated
        filter_date_start: requestQueryDateParser(savedSearchStore.startDate),
        filter_date_end: requestQueryDateParser(savedSearchStore.endDate),
        filter_pagerank_min: currentSearchFilter.filter_pagerank_min,
        filter_pagerank_max: currentSearchFilter.filter_pagerank_max,
        filter_languages: currentSearchFilter.filter_languages,
        web_category: currentSearchFilter.web_category,
        print_category: currentSearchFilter.print_category,
        filter_countries: currentSearchFilter.filter_countries,
        filter_relevance: currentSearchFilter.filter_relevance,
        filter_article_sentiment: currentSearchFilter.filter_article_sentiment,
        filter_included_domains: searchFiltersStore.filterIncludedDomains,
        filter_excluded_domains: searchFiltersStore.filterExcludedDomains,
        filter_keywords: savedSearchStore.filterKeywords,
        exclude_financial_news: currentSearchFilter.exclude_financial_news,
        exclude_social_mentions_with_low_reach:
          currentSearchFilter.exclude_social_mentions_with_low_reach,
        exclude_press_releases: currentSearchFilter.exclude_press_releases,
        exclude_market_research: currentSearchFilter.exclude_market_research,
        show_only_subscribed_markets:
          currentSearchFilter.show_only_subscribed_markets,
        web_media_type:
          searchFiltersStore.hasDigitalMediaTypeAccess &&
          currentSearchFilter.web_media_type,
        print_media_type:
          searchFiltersStore.hasPrintMediaTypeAccess &&
          currentSearchFilter.print_media_type,
        tv_media_type:
          searchFiltersStore.hasBroadcastMediaTypeAccess &&
          currentSearchFilter.tv_media_type,
        radio_media_type:
          searchFiltersStore.hasBroadcastMediaTypeAccess &&
          currentSearchFilter.radio_media_type,
        social_media_type:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type,
        facebook_media_type:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.facebook_media_type &&
          !socialStore.hasNoFacebookAccountConnected,
        instagram_media_type:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.instagram_media_type &&
          !socialStore.hasNoFacebookAccountConnected,
        twitter_media_type:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.twitter_media_type,
        youtube_media_type:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.youtube_media_type,
        verified_accounts_only:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.twitter_media_type &&
          currentSearchFilter.verified_accounts_only,
        hide_repost:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.twitter_media_type &&
          currentSearchFilter.hide_repost,
        include_comments:
          searchFiltersStore.hasSocialMediaTypeAccess &&
          currentSearchFilter.social_media_type &&
          currentSearchFilter.facebook_media_type &&
          currentSearchFilter.include_comments &&
          !socialStore.hasNoFacebookAccountConnected,
      },
      relationships: getRelationships(),
      type: searchType,
    },
  };

  if (searchType === "chart-searches") {
    addChartSearchFields(RequestQuery);
  } else if (searchType === "csv-article-searches") {
    addCsvArticleSearchFields(RequestQuery);
  } else if (searchType === "topic-searches") {
    addTopicSearchFields(RequestQuery);
  } else {
    addArticleSearchFields(RequestQuery);
  }
  return RequestQuery;
};
